import React, { useContext, useEffect, useState } from "react";
import { ConfirmDialog, useNotifications, FormFields, FormValidationSummary } from "sprint-common-web";

import { GetOrderDetailsResponseItem } from "../../../../common/api/orders-api/order/GetOrderDetailsResponse";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderStatus } from "../../../common/types/OrderStatus";
import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { OrderListingContext } from "../context/OrderListingContext";
import { OrderListingItem } from "../OrderListingItem";
import { validateOrder } from "./validateOrder";
import { VideoSelectionList, VideoSelectionListItem } from "./VideoSelectionList";

type SelectVideoDialogProps = {
    isOpen: boolean;
    item: OrderListingItem;
    onClose(): void;
};

export const SelectVideoDialog: React.VFC<SelectVideoDialogProps> = ({
    item,
    isOpen,
    onClose
}: SelectVideoDialogProps) => {
    const [isLoading, setLoading] = useState(false);
    const [videos, setVideos] = useState<VideoSelectionListItem[]>([]);
    const [contentSubtype, setContentSubtype] = useState<ContentSubtype>();
    const [selectedVideo, setSelectedVideo] = useState<string>();

    const { updateOrder } = useContext(OrderListingContext);
    const { selectRelevantVideo, getOrderDetails } = useOrdersModuleApi();
    const { success, error } = useNotifications();

    const hasError = validateOrder(item);
    const isSubmitDisabled = !videos.length || !selectedVideo || !hasError.isValid;

    useEffect(() => {
        if (isOpen) {
            setLoading(true);
            getOrderDetails(item.id)
                .then((res) => {
                    setContentSubtype(res.order.content.subtype);
                    setVideos(mapCloudAssetVideoToVideoAssetItems(res.order));
                    setSelectedVideo(res.order.cloudAssets.video.assetId);
                })
                .finally(() => setLoading(false));
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        if (!selectedVideo) return;

        try {
            await selectRelevantVideo({ orderId: item.id, assetId: selectedVideo });
            updateOrderAfterSelection();
            onClose();

            success("Selected video successfully");
        } catch {
            error("Cannot select video");
        }
    };

    const updateOrderAfterSelection = async () => {
        const { order: updatedOrder } = await getOrderDetails(item.id);

        updateOrder({
            id: updatedOrder.id,
            status: updatedOrder.status,
            subtitles: updatedOrder.cloudAssets.subtitles,
            video: updatedOrder.cloudAssets.video,
            isModified: true
        });
    };

    return (
        <ConfirmDialog
            title="Select relevant video"
            text={`Are you sure you want to select relevant video and ${
                item.status === OrderStatus.Pending ? "process" : "reprocess"
            } order?`}
            hidden={!isOpen}
            confirm={{ onClick: handleSubmit, disabled: isSubmitDisabled }}
            cancel={{ onClick: onClose }}
            width={900}
        >
            <FormFields>
                <FormValidationSummary validationResult={hasError} />
            </FormFields>
            <VideoSelectionList
                isLoading={isLoading}
                videos={videos}
                contentSubtype={contentSubtype}
                selectVideo={setSelectedVideo}
                selectedVideo={selectedVideo}
                targetResolution={item.targetResolution}
            />
        </ConfirmDialog>
    );
};

const mapCloudAssetVideoToVideoAssetItems = (order: GetOrderDetailsResponseItem): VideoSelectionListItem[] => {
    const result = [];

    const itemFromVideo: VideoSelectionListItem = {
        aspectRatio: order.cloudAssets.video.aspectRatio,
        assetId: order.cloudAssets.video.assetId,
        targetResolution: order.cloudAssets.video.targetResolution,
        sourceResolution: order.cloudAssets.video.sourceResolution,
        subtitle: order.cloudAssets.video.subtitle
    };

    if (order.cloudAssets.video.availability !== OrderAssetAvailability.NoMatch) {
        result.push(itemFromVideo);
    }

    const itemsFromNonMatchingVideos: VideoSelectionListItem[] = order.cloudAssets.video.nonMatchingVideos
        .filter((item) => item.subtitle.id === itemFromVideo.subtitle.id)
        .map((video) => ({
            aspectRatio: video.aspectRatio,
            assetId: video.assetId,
            targetResolution: order.cloudAssets.video.targetResolution,
            sourceResolution: video.resolution,
            subtitle: video.subtitle
        }));

    result.push(...itemsFromNonMatchingVideos);

    return result;
};
